/**
 * Chart data for the STO page, independent of the chart library: sources are mapped into
 * plain `[timestampMs, value]` points here, and only `components/ui/chart` knows Highcharts.
 */

export type ChartPoint = [number, number]

/** One bar from `/prices/stockmarketPrices`. */
export interface StockMarketBar {
  time: string
  open: number
  high: number
  low: number
  close: number
  volume?: number
  currency?: string
  closeInBTC?: number
  tickerSymbol?: string
  /** Data vendor, e.g. `polygon`. */
  provider?: string
}

export type ChartRangeId = '1D' | '7D' | '1M' | '6M' | '1Y' | 'YTD' | 'All'

const DAY_MS = 24 * 60 * 60 * 1000

/** Exclusive window start, measured back from the latest point (not "now"), so stale data still shows. */
const RANGE_START: Record<Exclude<ChartRangeId, 'All'>, (last: number) => number> = {
  '1D': (last) => last - DAY_MS,
  '7D': (last) => last - 7 * DAY_MS,
  '1M': (last) => last - 30 * DAY_MS,
  '6M': (last) => last - 182 * DAY_MS,
  '1Y': (last) => last - 365 * DAY_MS,
  // 1 ms earlier, so a point on 1 January itself is inside the (exclusive) window.
  YTD: (last) => Date.UTC(new Date(last).getUTCFullYear(), 0, 1) - 1,
}

const CHART_RANGES: ChartRangeId[] = ['1D', '7D', '1M', '6M', '1Y', 'YTD', 'All']

/** Sorted by time, invalid values dropped. */
export const toChartPoints = <T>(items: readonly T[], getTime: (item: T) => unknown, getValue: (item: T) => unknown) =>
  items
    .flatMap((item) => {
      const raw = getValue(item)
      // `Number(null)` is 0: a missing value must drop the point, not plot a crash to zero.
      if (raw === null || raw === undefined || raw === '') return []
      return [[new Date(getTime(item) as string).getTime(), Number(raw)] as ChartPoint]
    })
    .filter(([time, value]) => Number.isFinite(time) && Number.isFinite(value))
    .sort((a, b) => a[0] - b[0])

/**
 * Points in a range. The point just before the window is kept as the baseline, so e.g. 1D on
 * daily data compares the last close with the one before it.
 */
export const pointsInRange = (points: ChartPoint[], range: ChartRangeId): ChartPoint[] => {
  if (range === 'All' || points.length === 0) return points
  const start = RANGE_START[range](points[points.length - 1][0])
  const firstInside = points.findIndex(([time]) => time > start)
  if (firstInside === -1) return points.slice(-1)
  return points.slice(Math.max(0, firstInside - 1))
}

/**
 * Ranges worth offering: at least two points inside the window (so monthly data never shows
 * a "1D" that is really last month), and a view that differs from a wider one. `All` stays.
 */
export const availableRanges = (points: ChartPoint[]): ChartRangeId[] => {
  const seen = new Set<number>()
  return CHART_RANGES.filter((range) => {
    if (range === 'All') return points.length > 0
    const inRange = pointsInRange(points, range)
    const inside = inRange.filter(([time]) => time > RANGE_START[range](points[points.length - 1][0])).length
    if (inside < 2 || inRange.length === points.length || seen.has(inRange.length)) return false
    seen.add(inRange.length)
    return true
  })
}

export interface ChartChange {
  absolute: number
  /** Undefined when the baseline is 0. */
  percent?: number
}

export const changeBetween = (from: number, to: number): ChartChange => ({
  absolute: to - from,
  percent: from === 0 ? undefined : ((to - from) / Math.abs(from)) * 100,
})

/** Growth since the first point, in %: the fund "Cumulative" view. */
export const cumulativeReturns = (points: ChartPoint[]): ChartPoint[] => {
  const base = points[0]?.[1]
  if (!base) return []
  return points.map(([time, value]) => [time, (value / base - 1) * 100] as ChartPoint)
}

/** Return of each period against the previous point, in %: the fund "Monthly" view. */
export const periodReturns = (points: ChartPoint[]): ChartPoint[] =>
  points.slice(1).flatMap(([time, value], index) => {
    const previous = points[index][1]
    return previous ? [[time, (value / previous - 1) * 100] as ChartPoint] : []
  })

const YEAR_MS = 365 * DAY_MS
/** Month-end data can sit a few days short of a full year; this still counts as "a year ago". */
const YEAR_TOLERANCE_MS = 5 * DAY_MS

export interface ReturnStats {
  /** Since the first point, %. */
  cumulative?: number
  /** Against the value one year before the latest point, %. Needs a year of data. */
  yearOverYear?: number
  /**
   * Compound annual growth since the first point, %. Needs a year of data, or the start of operations:
   * then the return since the first point over the days from that start to the latest point (actual/365).
   */
  annualized?: number
}

const annualize = (growth: number, elapsed: number): number => (growth ** (YEAR_MS / elapsed) - 1) * 100

export const returnStats = (points: ChartPoint[], operationsStart?: number): ReturnStats => {
  if (points.length < 2) return {}
  const [firstTime, firstValue] = points[0]
  const [lastTime, lastValue] = points[points.length - 1]
  if (!firstValue) return {}

  const elapsed = lastTime - firstTime
  const yearAgo = [...points].reverse().find(([time]) => time <= lastTime - YEAR_MS + YEAR_TOLERANCE_MS)
  const hasYear = elapsed >= YEAR_MS - YEAR_TOLERANCE_MS

  return {
    cumulative: (lastValue / firstValue - 1) * 100,
    yearOverYear: hasYear && yearAgo?.[1] ? (lastValue / yearAgo[1] - 1) * 100 : undefined,
    annualized:
      operationsStart !== undefined
        ? lastTime > operationsStart
          ? annualize(lastValue / firstValue, Math.round((lastTime - operationsStart) / DAY_MS) * DAY_MS)
          : undefined
        : hasYear
          ? annualize(lastValue / firstValue, elapsed)
          : undefined,
  }
}

/** The picked range while the data still offers it, else the first available default, else `All`. */
export const resolveRange = (
  ranges: ChartRangeId[],
  picked: ChartRangeId | null,
  defaults: ChartRangeId[],
): ChartRangeId => (picked && ranges.includes(picked) ? picked : defaults.find((id) => ranges.includes(id))) ?? 'All'

export type FundView = 'cumulative' | 'monthly' | 'nav'

/** Fund views of NAV points: growth since the range start, return per month, or the NAV itself. */
export const fundViewPoints = (view: FundView, points: ChartPoint[]): ChartPoint[] => {
  if (view === 'cumulative') return cumulativeReturns(points)
  if (view === 'monthly') return periodReturns(points)
  return points
}

export type ChartTone = 'up' | 'down' | 'flat'

const toneOf = (percent: number | undefined): ChartTone =>
  percent === undefined || percent === 0 ? 'flat' : percent > 0 ? 'up' : 'down'

export interface ChartReadout {
  value: number
  percent?: number
  tone: ChartTone
  /** Direction of the whole range, which colours the line and does not change on hover. */
  rangeTone: ChartTone
}

/** Value and change to show: the hovered value, else the latest, against the range start. */
export const chartReadout = (points: ChartPoint[], hoverValue?: number): ChartReadout => {
  const start = points[0][1]
  const latest = points[points.length - 1][1]
  const value = hoverValue ?? latest
  const { percent } = changeBetween(start, value)
  return { value, percent, tone: toneOf(percent), rangeTone: toneOf(changeBetween(start, latest).percent) }
}

export interface ChartRow {
  time: number
  /** One value per series, in the order given; undefined where a series has no point that day. */
  values: Array<number | undefined>
}

/** Several series side by side, one row per timestamp, oldest first: tables and CSV files. */
export const joinByTime = (series: ChartPoint[][]): ChartRow[] => {
  const rows = new Map<number, Array<number | undefined>>()
  series.forEach((points, column) =>
    points.forEach(([time, value]) => {
      const row = rows.get(time) ?? new Array(series.length).fill(undefined)
      row[column] = value
      rows.set(time, row)
    }),
  )
  return [...rows].sort(([a], [b]) => a - b).map(([time, values]) => ({ time, values }))
}
